import './CopyTiger.scss';
import * as Dfns from 'date-fns/fp';
import { useEffect, useMemo, useState } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { Design, Size } from '@src/Components/designConstants';
import { DatePicker } from '../../Components/DatePicker';
import { EmptyState } from '../../Components/EmptyState';
import { Page } from '../../Components/Page';
import { PAGE_NAMES } from '../../Components/Page/page-constants';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useExperimentFlag } from '../../utils/experiment-context';
import { useTabbedNav } from '../../utils/hooks/useNav';
import {
  getCopyTigerGenerationTargets,
  getCopyTigerTrainingOptions,
  TableRow
} from './copyTigerUtils';
import { Dashboard } from './Dashboard';
import { Generate } from './Generate';
import { ModelConfig } from './ModelConfig';
import { RunReview } from './RunReview';
import { isViewingAsClient } from './runReviewUtils';
import { StyleGuide } from './StyleGuide';
import { TrainManager } from './TrainManager';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';

const PageTab = {
  STYLE_GUIDE: 'style-guide',
  GENERATE: 'generate',
  DASHBOARD: 'dashboard',
  MODELS: 'models',
  MODEL_CONFIG: 'model-config',
  REVIEW: 'review'
} as const;
type PageTabType = (typeof PageTab)[keyof typeof PageTab];

// Internal-only tabs. The Review tab is added only while a run is open (review/:runId), e.g. from
// the email link; there's no runs list to show without one.
const INTERNAL_NAVS = [
  { label: 'Train Models', key: PageTab.MODELS },
  { label: 'Style Guide', key: PageTab.STYLE_GUIDE },
  { label: 'Generate', key: PageTab.GENERATE }
];
const MODEL_CONFIG_NAV = { label: 'Model Config', key: PageTab.MODEL_CONFIG };

export const CopyTiger = () => {
  const { pathname, search } = useLocation();
  // Dev-only ?as=client demo: show the client's tabs too.
  const isInternal = useAppSelector(UserRedux.isInternalSelector) && !isViewingAsClient(search);
  const [isLoadingTrainingOptions, setIsLoadingTrainingOptions] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: Dfns.format('yyyy-MM-dd')(Dfns.subMonths(1, new Date())),
    end: TODAY
  });
  const [trainingOptionsMap, setTrainingOptionsMap] = useState<Record<string, TableRow[]>>({});
  // Generate tab uses a distinct dataset: every live ad group with existing RSA creative, regardless of
  // clicks/impressions (DSH-29971). Train tab stays on the performance-filtered trainingOptions above.
  const [isLoadingGenerationTargets, setIsLoadingGenerationTargets] = useState(false);
  const [generationTargetsMap, setGenerationTargetsMap] = useState<Record<string, TableRow[]>>({});
  const hasAdCopyV2 = Boolean(useExperimentFlag('enableAdCopyV2'));
  const reviewRunId = pathname.match(/\/adcopy-ai\/review\/([^/]+)/)?.[1];
  const canSeeModelConfig = isInternal && hasAdCopyV2;
  const canSeeReview = !isInternal || hasAdCopyV2;
  const navs = [
    ...(isInternal ? INTERNAL_NAVS : []),
    ...(canSeeModelConfig ? [MODEL_CONFIG_NAV] : []),
    ...(reviewRunId && canSeeReview ? [{ label: 'Review', key: PageTab.REVIEW }] : [])
  ];
  const { tab, goToTab } = useTabbedNav<PageTabType>({
    baseURL: 'adcopy-ai',
    defaultKey: isInternal ? PageTab.MODELS : PageTab.REVIEW,
    navs: isInternal
      ? [
          PageTab.MODELS,
          PageTab.STYLE_GUIDE,
          PageTab.GENERATE,
          PageTab.DASHBOARD,
          ...(canSeeModelConfig ? [PageTab.MODEL_CONFIG] : []),
          ...(canSeeReview ? [PageTab.REVIEW] : [])
        ]
      : [PageTab.REVIEW]
  });
  const companyInfo = useCompanyInfo();
  const setError = useSetError();

  const dataKey = useMemo(
    () => `${selectedDateRange.start}_${selectedDateRange.end}`,
    [selectedDateRange]
  );
  const trainingOptions = useMemo(() => trainingOptionsMap[dataKey], [dataKey, trainingOptionsMap]);
  const generationTargets = useMemo(
    () => generationTargetsMap[dataKey],
    [dataKey, generationTargetsMap]
  );

  useEffect(() => {
    (async () => {
      if (
        isInternal &&
        trainingOptionsMap[dataKey] == null &&
        !isLoadingTrainingOptions &&
        companyInfo.cid
      ) {
        setIsLoadingTrainingOptions(true);
        try {
          setTrainingOptionsMap({
            ...trainingOptionsMap,
            [dataKey]: await getCopyTigerTrainingOptions(
              companyInfo.cid,
              selectedDateRange.start,
              selectedDateRange.end
            )
          });
        } catch (e) {
          setTrainingOptionsMap({
            ...trainingOptionsMap,
            [dataKey]: []
          });
          setError({ message: e.message, reportError: e });
        } finally {
          setIsLoadingTrainingOptions(false);
        }
      }
    })();
  }, [
    companyInfo,
    isInternal,
    isLoadingTrainingOptions,
    dataKey,
    selectedDateRange,
    setError,
    trainingOptionsMap
  ]);

  useEffect(() => {
    (async () => {
      if (
        tab === PageTab.GENERATE &&
        generationTargetsMap[dataKey] == null &&
        !isLoadingGenerationTargets &&
        companyInfo.cid
      ) {
        setIsLoadingGenerationTargets(true);
        try {
          setGenerationTargetsMap({
            ...generationTargetsMap,
            [dataKey]: await getCopyTigerGenerationTargets(
              companyInfo.cid,
              selectedDateRange.start,
              selectedDateRange.end
            )
          });
        } catch (e) {
          setGenerationTargetsMap({
            ...generationTargetsMap,
            [dataKey]: []
          });
          setError({ message: e.message, reportError: e });
        } finally {
          setIsLoadingGenerationTargets(false);
        }
      }
    })();
  }, [
    companyInfo,
    isLoadingGenerationTargets,
    dataKey,
    generationTargetsMap,
    selectedDateRange,
    setError,
    tab
  ]);

  const tabContent = (() => {
    switch (tab) {
      case PageTab.MODELS:
        return (
          <TrainManager
            end={selectedDateRange.end}
            isLoadingTrainingOptions={isLoadingTrainingOptions}
            onDateRangeChange={setSelectedDateRange}
            start={selectedDateRange.start}
            trainingOptions={trainingOptions}
          />
        );
      case PageTab.STYLE_GUIDE:
        return <StyleGuide />;
      case PageTab.GENERATE:
        // Generate consumes generationTargets (all live ad groups) via the same props Train uses.
        return (
          <Generate
            isLoadingTrainingOptions={isLoadingGenerationTargets}
            trainingOptions={generationTargets}
          />
        );
      case PageTab.MODEL_CONFIG:
        return canSeeModelConfig && companyInfo.cid ? (
          <ModelConfig company={companyInfo.cid} />
        ) : null;
      case PageTab.DASHBOARD:
        return <Dashboard />;
      case PageTab.REVIEW:
        return (
          <Routes>
            <Route path='review/:runId' element={<RunReview />} />
            <Route
              path='*'
              element={
                <EmptyState
                  title='No run selected'
                  subtitle='Open a run from the link you were sent.'
                />
              }
            />
          </Routes>
        );
      default:
        return null;
    }
  })();

  return (
    <Page
      actions={
        tab === PageTab.GENERATE && (
          <DatePicker
            design={Design.NEUTRAL}
            datePresets
            onChange={setSelectedDateRange}
            range={selectedDateRange}
            removeHeaderText
            size={Size.SM}
          />
        )
      }
      pageType={PAGE_NAMES.ADCOPY_AI}
      title={PAGE_NAMES.ADCOPY_AI}
      navs={navs.length > 0 ? navs : undefined}
      selectedNav={tab}
      onNav={key => {
        // The Review tab only exists while its run is open; re-navigating to it would drop the runId.
        if (key !== PageTab.REVIEW) {
          goToTab(key as PageTabType);
        }
        Mixpanel.track(MxE.TabChange, { page: PAGE_NAMES.ADCOPY_AI, option_selected: key });
      }}
    >
      {tabContent}
    </Page>
  );
};
